import {
  Card,
  Center,
  Combobox,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  useCombobox,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import {
  type LightThirdPartyCase,
  type ThirdPartyCasesFilters,
  useLoadThirdPartyCases,
} from 'modules/ThirdpartyCases/api';
import { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { chunk } from 'remeda';
import { useBuildThirdpartyCaseTableColumns } from './hooks/use-build-thirdparty-case-table-columns';

export const ThirdpartyCaseTab = () => {
  const { t } = useTranslation();
  const profile = useProfile();
  const { id: debtorId } = useParams<{ id: string }>();

  const history = useHistory();

  const [pageIndex, setPageIndex] = useState(1);
  const [pageLimit, setPageLimit] = useState(profile.preferences.itemsPerPage);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [params] = useState<ThirdPartyCasesFilters>({
    page_limit: 1000,
    sort_order: 'asc',
    status: 'with_closed',
    debtor_id: String(debtorId),
    page: 1,
  });

  const { thirdPartyCases, isThirdPartyCasesLoading } = useLoadThirdPartyCases(params);

  const allItems = useMemo(() => thirdPartyCases?.data ?? [], [thirdPartyCases]);

  const pages = useMemo(() => chunk(allItems, pageLimit), [allItems, pageLimit]);

  const currentPageData = useMemo(() => {
    if (pages.length === 0) return [];
    const validIndex = Math.min(Math.max(pageIndex, 1), pages.length);
    return pages[validIndex - 1] ?? [];
  }, [pages, pageIndex]);

  const columns = useBuildThirdpartyCaseTableColumns();

  const table = useReactTable({
    data: currentPageData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalItems = allItems.length;
  const firstItem = totalItems === 0 ? 0 : (pageIndex - 1) * pageLimit + 1;
  const lastItem = Math.min(pageIndex * pageLimit, totalItems);

  const options = [10, 20, 50, 100].map(item => (
    <Combobox.Option value={item.toString()} key={item}>
      {item}
    </Combobox.Option>
  ));

  const openCase = (caseDetail: LightThirdPartyCase) => {
    history.push(`/thirdparty-cases/${caseDetail.id}`);
  };

  return (
    <Stack mt={'sm'}>
      <Card radius={8} p={0}>
        <Group style={{ overflowY: 'auto' }}>
          <Table stickyHeader fz={12}>
            <Table.Thead bg="purple.1">
              {table.getHeaderGroups().map(headerGroup => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <Table.Th key={header.id} c="purple.5">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map(row => (
                <Table.Tr
                  key={row.id}
                  onClick={() => openCase(row.original)}
                  style={{ cursor: 'pointer' }}
                >
                  {row.getVisibleCells().map(cell => (
                    <Table.Td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Group>

        {isThirdPartyCasesLoading && (
          <Center p="xl" w="100%">
            <Loader />
          </Center>
        )}

        <Group
          justify="space-between"
          p="sm"
          style={theme => ({
            borderTop: `1px solid ${theme.colors.gray[2]}`,
          })}
        >
          <Group>
            <Text size="sm">
              {firstItem} - {lastItem} {t(i18nKeys.OF)} {totalItems}
            </Text>
            <Combobox
              store={combobox}
              width={250}
              position="bottom-start"
              withArrow
              onOptionSubmit={val => {
                setPageLimit(Number(val));
                combobox.closeDropdown();
              }}
            >
              <Combobox.Target>
                <Group
                  onClick={() => combobox.toggleDropdown()}
                  style={{ cursor: 'pointer' }}
                  gap={4}
                >
                  <Text size="sm">
                    {pageLimit} {t(i18nKeys.PER_PAGE)}
                  </Text>
                  <IconChevronDown stroke={1.5} size={16} />
                </Group>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Options>{options}</Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          </Group>
          <Pagination size="sm" total={pages.length} value={pageIndex} onChange={setPageIndex} />
        </Group>
      </Card>
    </Stack>
  );
};
